import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { cn } from "../lib/utils";
import {
  History,
  User,
  ArrowRight,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Users,
  DollarSign,
} from "lucide-react";

const ChangeHistoryBlock = ({
  history = [],
  title = "Change History",
  compact = false,
  maxItems = 10,
  bare = false, // render inner content only (no Card wrapper) — for embedding in a tab/section
}) => {
  const formatDateTime = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getFieldIcon = (field) => {
    switch (field) {
      case "status":
      case "status":
        return <CheckCircle2 className="h-3 w-3" />;
      case "phase":
        return <AlertTriangle className="h-3 w-3" />;
      case "client_id":
      case "client_name":
        return <Users className="h-3 w-3" />;
      case "project_manager_id":
        return <User className="h-3 w-3" />;
      case "deadline":
      case "start_date":
        return <Calendar className="h-3 w-3" />;
      case "budget":
      case "probability":
        return <DollarSign className="h-3 w-3" />;
      default:
        return <FileText className="h-3 w-3" />;
    }
  };

  const getFieldLabel = (field) => {
    const labels = {
      status: "Deal Status",
      status: "Status",
      phase: "Phase",
      client_id: "Client",
      client_name: "Client",
      project_manager_id: "Project Manager",
      deadline: "Deadline",
      start_date: "Start Date",
      budget: "Budget",
      probability: "Probability",
      name: "Name",
    };
    return labels[field] || field?.replace(/_/g, " ");
  };

  const getChangeTypeColor = (changeType, field) => {
    if (changeType === "status_change" || field === "status" || field === "status") {
      return "text-green-400";
    }
    if (field === "phase") {
      return "text-purple-400";
    }
    if (field === "deadline" || field === "start_date") {
      return "text-orange-400";
    }
    return "text-sky-400";
  };

  const displayedHistory = [...history].reverse().slice(0, maxItems);

  if (!history || history.length === 0) {
    const emptyBody = <p className="text-sm text-zinc-500 italic">No changes recorded yet</p>;
    if (bare) return emptyBody;
    return (
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className={cn("pb-1", compact ? "pt-2 px-3" : "pt-3 px-4")}>
          <CardTitle className={cn("text-zinc-50 flex items-center gap-2", compact ? "text-sm" : "text-base")}>
            <History className={cn("text-purple-400", compact ? "h-4 w-4" : "h-5 w-5")} />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className={cn(compact ? "pt-1 pb-2 px-3" : "pt-2 pb-3 px-4")}>
          {emptyBody}
        </CardContent>
      </Card>
    );
  }

  const listBody = (
        <div className={cn("space-y-1.5 max-h-64 overflow-y-auto pr-1", compact && "max-h-48")}>
          {displayedHistory.map((entry, idx) => (
            <div 
              key={idx} 
              className="flex items-start gap-2 text-xs border-l-2 border-zinc-700 pl-2 py-1 hover:bg-zinc-800/30 rounded-r"
            >
              <div className="flex-1 min-w-0">
                {/* Change description */}
                {entry.description ? (
                  <p className={cn("font-medium", getChangeTypeColor(entry.change_type, entry.field))}>
                    {entry.description}
                  </p>
                ) : (
                  <div className="flex items-center gap-1 flex-wrap">
                    {getFieldIcon(entry.field)}
                    <span className="text-zinc-400 capitalize">{getFieldLabel(entry.field)}:</span>
                    {entry.old_value && (
                      <>
                        <span className="text-zinc-500 truncate max-w-[100px]">{entry.old_value}</span>
                        <ArrowRight className="h-3 w-3 text-zinc-600 flex-shrink-0" />
                      </>
                    )}
                    <span className={cn("font-medium truncate max-w-[120px]", getChangeTypeColor(entry.change_type, entry.field))}>
                      {entry.new_value || "—"}
                    </span>
                  </div>
                )}
                
                {/* Meta info */}
                <div className="flex items-center gap-2 text-zinc-500 text-[10px] mt-0.5">
                  <User className="h-2.5 w-2.5" />
                  <span>{entry.changed_by}</span>
                  <span>•</span>
                  <span>{formatDateTime(entry.changed_at)}</span>
                </div>
              </div>
            </div>
          ))}
          
          {history.length > maxItems && (
            <p className="text-[10px] text-zinc-500 text-center py-1">
              + {history.length - maxItems} more changes
            </p>
          )}
        </div>
  );

  if (bare) return listBody;

  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardHeader className={cn("pb-1", compact ? "pt-2 px-3" : "pt-3 px-4")}>
        <div className="flex items-center justify-between">
          <CardTitle className={cn("text-zinc-50 flex items-center gap-2", compact ? "text-sm" : "text-base")}>
            <History className={cn("text-purple-400", compact ? "h-4 w-4" : "h-5 w-5")} />
            {title}
          </CardTitle>
          <span className="text-xs text-zinc-500">{history.length} changes</span>
        </div>
      </CardHeader>
      <CardContent className={cn(compact ? "pt-1 pb-2 px-3" : "pt-2 pb-3 px-4")}>
        {listBody}
      </CardContent>
    </Card>
  );
};

export default ChangeHistoryBlock;
